import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import { Vehicle } from '../../types';
import { api, technicalNoteSettingsService } from '../../services/api';

interface NoteFormProps {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const NoteForm: React.FC<NoteFormProps> = ({
  vehicle,
  open,
  onClose,
  onSuccess
}) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [noteTypes, setNoteTypes] = useState<any[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(false);

  useEffect(() => {
    if (open) {
      loadNoteTypes();
    }
  }, [open]);

  const loadNoteTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await technicalNoteSettingsService.getSettings();
      const settings = response.data.data;
      setNoteTypes(settings.types || []);
    } catch (error) {
      console.error('Error loading note types:', error);
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleTemplateClick = (template: string) => {
    setNote(prev => {
      if (prev) {
        return `${prev}\n${template}`;
      }
      return template;
    });
  };

  const handleSubmit = async () => {
    if (!note.trim()) {
      setError('Veuillez saisir une note');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post(`/vehicles/${vehicle.id}/notes`, {
        content: note.trim()
      });

      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error creating note:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création de la note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6">Ajouter Note Technique</Typography>
        <Typography variant="body2" color="text.secondary">
          {vehicle.registration_number} • {vehicle.marque} {vehicle.modele}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <TextField
          fullWidth
          multiline
          rows={6}
          label="Note technique"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Décrivez l'observation ou l'intervention..."
          autoFocus
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Types de notes techniques:
          </Typography>
          {loadingTypes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {noteTypes.map((type) => (
                <Chip
                  key={type.id}
                  label={type.name}
                  onClick={() => handleTemplateClick(type.name)}
                  sx={{ cursor: 'pointer' }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !note.trim()}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Enregistrer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NoteForm;

